//import React, { useEffect } from 'react';
import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';

export default function Home() {
  const [value, setValue] = React.useState<string | null>(null);
  const [options, setOptions] = React.useState<string[]>([]);
  const [inputValue, setInputValue] = React.useState('');

  // Debounce function
  const debounce = (func: Function, wait: number) => {
    let timeout: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // Cached results
  const searchCache = React.useRef<Record<string, string[]>>({});

  const runSearch = (query: string) => {
    const q = query.trim();
    if (!q) return;
    window.location.href = `/search?q=${encodeURIComponent(q)}`;
  };

  // Debounced autocomplete function
  const fetchAutoComplete = React.useCallback(
    debounce(async (searchTerm: string) => {
      // Don't search for less than 2 characters
      if (searchTerm.length < 2) {
        setOptions([]);
        return;
      }

      // Check cache first
      if (searchCache.current[searchTerm]) {
        setOptions(searchCache.current[searchTerm]);
        return;
      }

      try {
        const response = await fetch(`/search/autocomplete?q=${encodeURIComponent(searchTerm)}`);
        const data = await response.json();
        const completions: string[] = Array.isArray(data?.completions) ? data.completions : [];
        // Cache the results
        searchCache.current[searchTerm] = completions;
        setOptions(completions);
      } catch (error) {
        console.error('Error fetching autocomplete:', error);
        setOptions([]);
      }
    }, 300), // 300ms delay
    []
  );

  return (
    <>
      <style>
        {`.home-container {
            max-width: 800px;
            margin: 0 auto;
            padding: 2rem;
          }
          .search-section {
            text-align: center;
            margin-bottom: 2rem;
          }
          .search-section h1 {
            white-space: nowrap;
            font-size: clamp(1.5rem, 4vw, 2rem);
          }
          .search-form {
            display: flex;
            gap: 0.5rem;
            align-items: center;
          }
          .search-button {
            background-color: #007bff;
            color: white;
            padding: 10px;
          }
          .search-button:hover {
            background-color: #0056b3;
          }
          .dark-mode .home-container,
          .dark-mode .search-section h1 {
            color: #ffffff;
          }
          .dark-mode .search-button {
            background-color: #9a55ff;
            color: #ffffff;
          }
          .dark-mode .search-button:hover {
            background-color: #7b39db;
          }
          .dark-mode .home-container .MuiFormLabel-root {
            color: #9c9fa6;
          }
          .dark-mode .home-container .MuiFormLabel-root.Mui-focused {
            color: #b66dff;
          }
          .dark-mode .home-container .MuiOutlinedInput-root {
            background-color: #222328;
            color: #ffffff;
          }
          .dark-mode .home-container .MuiOutlinedInput-root fieldset {
            border-color: #322f2f;
          }
          .dark-mode .home-container .MuiOutlinedInput-root:hover fieldset {
            border-color: #9c9fa6;
          }
          .dark-mode .home-container .MuiOutlinedInput-root.Mui-focused fieldset {
            border-color: #b66dff;
          }
          .dark-mode .home-container .MuiAutocomplete-popupIndicator,
          .dark-mode .home-container .MuiAutocomplete-clearIndicator {
            color: #9c9fa6;
          }
          .dark-mode .home-container .MuiInputBase-input::placeholder {
            color: #c9c8c8;
            opacity: 1;
          }`}
      </style>
      <div className="home-container">
        <div className="search-section">
          <h1>Search Decode Investing</h1>
          <form className="search-form" action="/search" method="get" onSubmit={(e) => {
            e.preventDefault();
            runSearch(inputValue);
          }}>
            <Autocomplete<string, false, false, true>
              value={value}
              onChange={(event: React.SyntheticEvent, newValue: string | null, reason) => {
                setValue(newValue);
                if (typeof newValue === 'string') {
                  setInputValue(newValue);
                  if (reason === 'selectOption') {
                    runSearch(newValue);
                  }
                }
              }}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
                fetchAutoComplete(newInputValue);
              }}
              selectOnFocus
              clearOnBlur={false}
              handleHomeEndKeys
              id="stock-search"
              options={options}
              getOptionLabel={(option) => option}
              fullWidth
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="q"
                  label="Search stocks, pages, and questions"
                  variant="outlined"
                  sx={{ flexGrow: 1 }}
                  autoComplete="off"
                />
              )}
            />
            <IconButton type="submit" className="search-button" aria-label="search">
              <SearchIcon />
            </IconButton>
          </form>
        </div>
      </div>
    </>
  );
}
